import axios, { HttpStatusCode } from 'axios';
import { modalHelpers } from 'presentation/components/Modal/Modal.service';
import { AUTH_ROUTES } from 'presentation/router/CONSTANTS';
import queryString from 'query-string';
import getMessage from 'utilities/constants/MESSAGE';
import { commonHelpers } from 'utilities/helpers';
import { UserStorage } from '../storages/UserStorage';
import { ENV, TYPE_CONTENT } from './CONSTANTS';
// https://lightrains.com/blogs/axios-intercepetors-react/

const IGNORE_SHOW_ALERT_API = [
    '/auth/logout',
    'chat/unread-message',
    'notification/unread-notices',
    'chat/chat-room',
];

class AxiosConfig {
    private controller;
    private userStorage;
    private axiosConfig;
    private countFetching;

    constructor() {
        this.controller = new AbortController();
        this.userStorage = new UserStorage();
        this.countFetching = 0;
        this.axiosConfig = this.initialAxiosInstance(this);
        this.initInterceptor(this);
    }

    initialAxiosInstance(self: AxiosConfig) {
        return axios.create({
            baseURL: process.env.REACT_APP_API_END_POINT,
            timeout: ENV.TIMEOUT,
            signal: self.controller.signal,
            paramsSerializer: (params) => queryString.stringify(params),
        });
    }

    initInterceptor(self: AxiosConfig) {
        self.axiosConfig.interceptors.request.use(
            (config) => {
                self.countFetching++;
                if (self.countFetching === 1) {
                    // calling api
                    window.addEventListener('popstate', () => self.abortFetching());
                }
                const token = self.userStorage.getAccessToken();
                if (token && config.headers) {
                    config.headers['Authorization'] = 'Bearer ' + token;
                } else {
                    config.headers['Authorization'] = 'Bearer ';
                }
                config.headers['Accept'] = TYPE_CONTENT.APPLICATION_JSON;
                return config;
            },
            (error) => {
                Promise.reject(error);
            },
        );

        self.axiosConfig.interceptors.response.use(
            (response) => {
                self.countFetching--;
                if (self.countFetching === 0) {
                    window.removeEventListener('popstate', () => self.abortFetching());
                }
                return response;
            },
            async (error) => {
                self.countFetching--;

                if (self.countFetching === 0) {
                    window.removeEventListener('popstate', () => self.abortFetching());
                }
                if (!window.navigator.onLine) {
                    window.location.reload();
                    return;
                }

                //connection Timeout
                if (error?.code === 'ECONNABORTED') {
                    self.controller.abort(); // stop other when
                    self.resetConfig();
                    modalHelpers.showAlert(commonHelpers.getAlertPropsByErrCode('N0945'));
                    return Promise.reject(error);
                } else if (error.response) {
                    // The request was made and the server responded with a status code
                    // that falls out of the range of 2xx
                    if (error.response.status === HttpStatusCode.Unauthorized) {
                        return this.processUnauthError(self);
                    } else if (error.response.status === HttpStatusCode.NotAcceptable) {
                        return this.processError(error, self);
                    } else if (error.response.status === HttpStatusCode.InternalServerError) {
                        if (
                            !IGNORE_SHOW_ALERT_API.find((url: string) => {
                                return error.request?.responseURL?.indexOf(url) > -1;
                            })
                        ) {
                            self.controller.abort(); // stop other when
                            self.resetConfig();
                            modalHelpers.showAlert(commonHelpers.getAlertPropsByErrCode('N0944'));
                            return Promise.reject(error);
                        }
                        throw error.response.data ? error.response.data : error.response;
                    } else {
                        throw error.response.data ? error.response.data : error.response;
                    }
                } else {
                    // Something happened in setting up the request that triggered an Error
                    throw error?.response?.data ? error.response.data : error.response;
                }
            },
        );
    }

    public async processUnauthError(self: AxiosConfig) {
        self.controller.abort(); // stop other when
        try {
            const response = await axios.post(
                '/auth/refresh',
                {
                    refreshToken: self.userStorage.getRefreshToken(),
                    userId: self.userStorage.getUserInfo()?.user?.id,
                },
                {
                    headers: {
                        Authorization: 'Bearer ' + self.userStorage.getAccessToken(), //the token is a variable which holds the token
                    },
                    baseURL: process.env.REACT_APP_API_END_POINT,
                    timeout: ENV.TIMEOUT,
                },
            );
            if(response?.data?.result){
                self.userStorage.updateAccessToken(response.data.result);
                window.location.reload();
            } else {
                self.userStorage.removeUserInfo();
                window.localStorage.removeItem('user');
                window.location.replace(AUTH_ROUTES.PATH + '/' + AUTH_ROUTES.SUB.CU_001_04);
            }
        } catch (error) {
            self.userStorage.removeUserInfo();
            window.localStorage.removeItem('user');
            window.location.replace(AUTH_ROUTES.PATH + '/' + AUTH_ROUTES.SUB.CU_001_04);
            return Promise.reject(error);
        }
    }

    public async processError(error: any, self: AxiosConfig) {
        self.controller.abort(); // stop other when
        modalHelpers.showAlert({
            message: getMessage('IS_BLACK_USER'),
            type: 'warning',
            okText: '',
            cancelText: 'アプリを閉じる',
            onOk: () => {
                self.userStorage.removeUserInfo();
                window.location.replace(AUTH_ROUTES.PATH + '/' + AUTH_ROUTES.SUB.CU_001_00);
            },
        });
        return Promise.reject({
            ...error,
            message: '',
        });
    }

    public abortFetching() {
        // this.controller.abort();
        this.resetConfig();
    }

    public resetConfig() {
        this.controller = new AbortController();
        this.axiosConfig = this.initialAxiosInstance(this);
        this.initInterceptor(this);
    }

    getAxiosConfig() {
        return this.axiosConfig;
    }
}

const axiosConfig = new AxiosConfig();
export default axiosConfig;
